import { ArrowUp } from 'lucide-react'
import type { ReactNode } from 'react'

import { Markdown } from '@/components/ui'
import { type Analysis, hasAnalysisGenerated } from '@/modules/analysis'
import { humanizeText } from '@/utils'
import type { Report, ReportSection } from '../../stores/types'
import { DownloadMenu } from './DownloadMenu'
import { useReportExport } from './export/useReportExport'
import { formatIsoDate } from './format'
import { sectionAnchorId, stripDuplicateTitleHeading } from './headings'
import { ReportSectionBlock } from './ReportSectionBlock'
import { REPORT_PROSE } from './sectionProse'

const RECOMMENDATIONS_SECTION_ID = 'conclusion_and_recommendations'
const REPORT_TOP_ID = 'ai-report-top'

interface AnalysisReportSectionProps {
  slug: string
  analysis: Analysis
}

// A single document sheet, read like a filed expert report. Order is what
// an attorney needs: masthead, recommendations panel, executive summary,
// then the numbered analysis sections. No table of contents — the report
// is the page.
export default function AnalysisReportSection({
  slug,
  analysis,
}: AnalysisReportSectionProps) {
  const report = analysis.report
  const { exporting, exportPdf, exportDocx } = useReportExport(
    report,
    `${slug}-ai-analysis`,
  )

  if (!hasAnalysisGenerated(analysis) || !report) return null

  const counsel = report.sections.find(isRecommendationsSection)
  const bodySections = report.sections.filter(
    (section) => !isRecommendationsSection(section),
  )
  const sectionLinks = buildSectionLinks(bodySections)

  const exportButton = (
    <DownloadMenu
      label={exporting ? 'Preparing…' : 'Download report'}
      options={[
        { label: 'Download as PDF', onSelect: exportPdf },
        { label: 'Download as Word (.docx)', onSelect: exportDocx },
      ]}
    />
  )

  return (
    <div className="max-w-6xl">
      <article
        id={REPORT_TOP_ID}
        className="overflow-hidden rounded-lg border bg-white"
      >
        <div className="flex items-center justify-between gap-4 border-b px-6 py-3 sm:px-10">
          <span className="text-[13px] font-medium text-ink-subtle">
            AI analysis
            {report.case_type ? `: ${humanizeText(report.case_type)}` : ''}
          </span>
          {exportButton}
        </div>

        <div className="px-6 py-8 sm:px-10 sm:py-10">
          <ReportMasthead report={report} />

          {counsel && (
            <ReportSectionBlock
              slug={slug}
              section={counsel}
              variant="counsel"
              sectionLinks={sectionLinks}
            />
          )}

          {report.executive_summary?.trim() && (
            <section className="mt-12">
              <h2 className="mb-4 text-pretty text-2xl font-semibold leading-snug tracking-tight text-blue-7">
                Executive Summary
              </h2>
              <Markdown
                content={stripDuplicateTitleHeading(
                  report.executive_summary,
                  'Executive Summary',
                )}
                componentsOverride={REPORT_PROSE}
              />
            </section>
          )}

          {bodySections.map((section, index) => (
            <ReportSectionBlock
              key={section.id ?? `section-${index}`}
              slug={slug}
              section={section}
              number={index + 1}
            />
          ))}

          <ReportFooter action={exportButton} />
        </div>
      </article>
    </div>
  )
}

function ReportFooter({ action }: { action: ReactNode }) {
  return (
    <footer className="mt-14 flex flex-col items-center gap-4 border-t border-border pt-8 text-center sm:flex-row sm:justify-between sm:text-left">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-blue-7">End of report</p>
        <button
          type="button"
          onClick={scrollToReportTop}
          className="inline-flex items-center gap-1 text-sm text-brand underline-offset-2 hover:underline cursor-pointer"
        >
          <ArrowUp className="size-3.5" aria-hidden />
          Back to top
        </button>
      </div>
      {action}
    </footer>
  )
}

function scrollToReportTop() {
  document
    .getElementById(REPORT_TOP_ID)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Maps each body section's anchor id to its heading so a recommendation
// can link to the section its evidence lives in.
function buildSectionLinks(sections: ReportSection[]): Record<string, string> {
  const links: Record<string, string> = {}
  for (const section of sections) {
    links[sectionAnchorId(section)] = section.title
  }
  return links
}

function isRecommendationsSection(section: ReportSection): boolean {
  return (
    (section.id ?? '').trim().toLowerCase().replace(/-/g, '_') ===
    RECOMMENDATIONS_SECTION_ID
  )
}

function ReportMasthead({ report }: { report: Report }) {
  const stats = mastheadStats(report)
  return (
    <header className="border-b border-border pb-8">
      {report.generated_at && (
        <p className="text-sm text-muted-foreground">
          Created {formatIsoDate(report.generated_at)}
        </p>
      )}
      <h1 className="mt-2 text-pretty text-3xl font-semibold leading-tight tracking-tight text-blue-7 sm:text-4xl">
        {report.title?.trim() || 'Forensic Accounting Report'}
      </h1>
      {stats.length > 0 && (
        // @container so the tiles react to the width actually available,
        // which shrinks when the chat panel opens, not the viewport.
        <div className="@container mt-8">
          <dl className="grid grid-cols-1 gap-3 @sm:grid-cols-3">
            {stats.map((stat) => (
              <StatTile key={stat.label} stat={stat} />
            ))}
          </dl>
        </div>
      )}
    </header>
  )
}

function StatTile({ stat }: { stat: MastheadStat }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-5 py-4">
      <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
        {stat.label}
      </dt>
      <dd className="mt-2 text-pretty text-2xl font-semibold leading-tight tracking-tight text-blue-7 tabular-nums">
        <span className="block">
          {stat.value}
          {stat.endValue && <span className="text-muted-foreground"> –</span>}
        </span>
        {stat.endValue && <span className="block">{stat.endValue}</span>}
      </dd>
      {stat.detail && (
        <p className="mt-1 text-xs font-medium text-muted-foreground tabular-nums">
          {stat.detail}
        </p>
      )}
    </div>
  )
}

interface MastheadStat {
  label: string
  value: string
  // A second value on its own line, e.g. the end of a date range, so a
  // long "start – end" string never wraps in the middle of a date.
  endValue?: string
  detail?: string
}

function mastheadStats(report: Report): MastheadStat[] {
  const coverage = report.coverage
  if (!coverage) return []
  const stats: MastheadStat[] = []

  if (coverage.date_from && coverage.date_to) {
    stats.push({
      label: 'Period covered',
      value: formatIsoDate(coverage.date_from),
      endValue: formatIsoDate(coverage.date_to),
    })
  }
  if (coverage.transaction_count) {
    stats.push({
      label: 'Transactions reviewed',
      value: coverage.transaction_count.toLocaleString(),
    })
  }
  if (coverage.accounts?.length) {
    stats.push(accountsStat(coverage.accounts))
  }
  return stats
}

// A report can cover 20+ accounts. The tile leads with the count; the
// last-4 list rides underneath as a subtitle only while it stays short.
const ACCOUNT_DETAIL_LIMIT = 4

function accountsStat(accounts: string[]): MastheadStat {
  return {
    label: accounts.length === 1 ? 'Account reviewed' : 'Accounts reviewed',
    value: accounts.length.toLocaleString(),
    detail:
      accounts.length <= ACCOUNT_DETAIL_LIMIT
        ? accounts.map((last4) => `••${last4}`).join(', ')
        : undefined,
  }
}
